import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import AddIcon from '@mui/icons-material/Add';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import * as subMatTemplateActions from '../../store/subMatTemplate';
import * as gclientActions from '../../store/gclient';
import { AlertDialog, ConfirmDialog, DialogTitleClose } from '../dialog';
import SubMatTemplateFolderPicker from './SubMatTemplateFolderPicker';
import SubMatTemplateTocTree from './SubMatTemplateTocTree';
import SubMatTemplateTocPreview from './SubMatTemplateMergedTocPreview';
import SubMatTemplateSendSection from './SubMatTemplateSendSection';
import {
  SubMatTemplateTocDocFrame,
  SubMatTemplateTocPreviewTitle,
  useSubMatTocDocPreview,
} from './SubMatTemplateTocDocPreview';
import SubMatTemplateDialogFooter from './SubMatTemplateDialogFooter';
import SubMatDialogFooterButton from './SubMatDialogFooterButton';
import CertifiedStampPrintPane from './CertifiedStampPrintPane';
import CertifiedStampPlacementDialog from './CertifiedStampPlacementDialog';
import {
  buildMergePreviewPayload,
  normalizeBodyHeaderPaging,
  normalizeTocNumbering,
  repairTemplateItemLinksForDisplay,
} from './subMatTemplateTocOrder';
import { stampPlacementsToApi, stampPlacementsToUi } from './subMatStampPlacements';
import {
  STAMP_LAYOUT_COMPACT,
  STAMP_LAYOUT_FULL,
  normalizeStampLayout,
} from './certifiedStampSpec';

const TOC_PANEL_WIDTH = '75%';
const TOC_BOOKMARK_WIDTH = 28;
const STEP1_WORK_MIN_HEIGHT = 520;
const DIALOG_PAPER_SX = {
  width: { xs: 'calc(100vw - 32px)', md: 'min(1280px, calc(100vw - 64px))' },
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
};
/** 목차 책갈피 + 패널 최외곽 (흰 글자 대비, 구성·미리보기 카드와 구분) */
const TOC_SHELL_BG = 'grey.500';
/** 패널 안쪽 여백 — 책갈피 상단과 목차구성 카드 상단 정렬에 사용 */
const TOC_SHELL_PAD = 8;

const normalizeSignOption = (value) => (
  value === 'NONE' ? 'NONE' : 'CERTIFIED_TRUE_COPY_AND_SIGN'
);

const stampFormDefaults = {
  supplierLabel: '제공사',
  supplier: '',
  recipient: '',
  site: '',
  phrase: '',
};

/**
 * 기존 목차는 그대로 두고, 없는 folderId만 맨 아래(루트)에 추가.
 * 새 항목끼리의 상대 계층은 유지하되, 기존 목차 노드 아래로는 붙이지 않음(안전 추가 → 드래그).
 */
const appendNewItemsSafely = (built, existing) => {
  const prev = (existing || []).map((it) => ({ ...it }));
  const existingFolderIds = new Set(prev.map((it) => it.folderId).filter(Boolean));
  const builtList = built || [];

  const newcomers = builtList.filter(
    (b) => b.folderId && !existingFolderIds.has(b.folderId),
  );
  if (!newcomers.length) {
    return { items: prev, addedCount: 0 };
  }

  const idByBuiltId = {};
  newcomers.forEach((b) => {
    idByBuiltId[b.id] = b.id || uuidv4();
  });

  // 첫 반영: 서류 트리 계층 그대로 초안
  if (!prev.length) {
    const linked = newcomers.map((b, i) => {
      const id = idByBuiltId[b.id];
      const parentItemId = b.parentItemId && idByBuiltId[b.parentItemId]
        ? idByBuiltId[b.parentItemId]
        : null;
      return {
        ...b,
        id,
        parentItemId,
        included: b.included !== false,
        includeTestComparison: b.includeTestComparison === true,
        sortOrder: b.sortOrder ?? i + 1,
      };
    });
    return { items: linked, addedCount: linked.length };
  }

  // 추가 반영: 신규끼리만 부모 연결, 그 외는 루트 맨 아래
  let rootSort = prev
    .filter((it) => !it.parentItemId)
    .reduce((max, it) => Math.max(max, it.sortOrder || 0), 0);

  const siblingSort = {};
  const added = newcomers.map((b) => {
    const id = idByBuiltId[b.id];
    const parentIsAlsoNew = b.parentItemId && idByBuiltId[b.parentItemId];
    let parentItemId = null;
    let sortOrder;

    if (parentIsAlsoNew) {
      parentItemId = idByBuiltId[b.parentItemId];
      siblingSort[parentItemId] = (siblingSort[parentItemId] || 0) + 1;
      sortOrder = siblingSort[parentItemId];
    } else {
      parentItemId = null;
      rootSort += 1;
      sortOrder = rootSort;
    }

    return {
      ...b,
      id,
      parentItemId,
      sortOrder,
      included: b.included !== false,
      includeTestComparison: b.includeTestComparison === true,
    };
  });

  return { items: [...prev, ...added], addedCount: added.length };
};

const SubMatTemplateEditDialog = ({
  open,
  onClose,
  crudMode,
  template,
  folders,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector((s) => s.session.sessionUser);
  const isCreate = crudMode === 'C';
  const [name, setName] = useState('');
  const [memo, setMemo] = useState('');
  /** null | 'createSave' | 'editMeta' */
  const [metaDialogMode, setMetaDialogMode] = useState(null);
  const [metaName, setMetaName] = useState('');
  const [metaMemo, setMetaMemo] = useState('');
  const [metaErrors, setMetaErrors] = useState([]);
  const metaDialogOpen = Boolean(metaDialogMode);
  const [tocNumbering, setTocNumbering] = useState('DECIMAL_HIERARCHY');
  const [bodyHeaderPaging, setBodyHeaderPaging] = useState(true);
  const [items, setItems] = useState([]);
  const [signOption, setSignOption] = useState('CERTIFIED_TRUE_COPY_AND_SIGN');
  const [stampLayout, setStampLayout] = useState(STAMP_LAYOUT_FULL);
  const [emptyRecipient, setEmptyRecipient] = useState(false);
  const [emptySite, setEmptySite] = useState(false);
  const [stampPlacements, setStampPlacements] = useState({});
  const [stampPlacementOpen, setStampPlacementOpen] = useState(false);
  const [signThumbnailUrl, setSignThumbnailUrl] = useState('');
  const [pickerIds, setPickerIds] = useState([]);
  const [errors, setErrors] = useState([]);
  const [info, setInfo] = useState('');
  const [alertInfo, setAlertInfo] = useState({ open: false, titleAlert: '', messageAlert: '' });
  const [pdfEmptyConfirmOpen, setPdfEmptyConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [tocHelpAnchor, setTocHelpAnchor] = useState(null);
  const [memoPopoverAnchor, setMemoPopoverAnchor] = useState(null);
  /** 1: 서류·목차 / 2: 원본대조필 */
  const [step, setStep] = useState(1);
  /** 1페이지 오른쪽 목차 패널(책갈피로 접기/펼치기) */
  const [tocPanelOpen, setTocPanelOpen] = useState(false);
  const thumbnailUrlRef = useRef('');
  const tocDocPreview = useSubMatTocDocPreview(items, open);

  const { control, reset, getValues } = useForm({
    defaultValues: stampFormDefaults,
  });

  useEffect(() => {
    if (open) {
      const paging = normalizeBodyHeaderPaging(template?.bodyHeaderPaging);
      const phrases = sessionUser?.phrases || {};
      setName(template?.name || '');
      setMemo(template?.memo || '');
      setMetaDialogMode(null);
      setMetaName('');
      setMetaMemo('');
      setMetaErrors([]);
      setMemoPopoverAnchor(null);
      setTocNumbering(normalizeTocNumbering(template?.tocNumbering));
      setBodyHeaderPaging(paging);
      setSignOption(normalizeSignOption(template?.signOption));
      setStampLayout(normalizeStampLayout(template?.stampLayout));
      setEmptyRecipient(false);
      setEmptySite(false);
      const raw = template?.items ? template.items.map((it) => ({ ...it })) : [];
      const repaired = repairTemplateItemLinksForDisplay(raw, folders);
      setItems(repaired);
      setStampPlacements(stampPlacementsToUi(template?.stampPlacements, paging));
      setStampPlacementOpen(false);
      setPickerIds([]);
      setErrors([]);
      setInfo('');
      setPdfEmptyConfirmOpen(false);
      setStep(1);
      setTocPanelOpen(repaired.length > 0);
      setTocHelpAnchor(null);
      reset({
        supplierLabel: phrases.supplierLabel || '제공사',
        supplier: phrases.supplier || '',
        recipient: '',
        site: '',
        phrase: phrases.phrase || '',
      });
    }
  }, [open, template, folders, sessionUser, reset]);

  useEffect(() => {
    if (!open || !sessionUser?.signDocumentPath) {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current);
        thumbnailUrlRef.current = '';
      }
      setSignThumbnailUrl('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await gclientActions.downloadDirect(sessionUser.signDocumentPath);
        const blob = await res.blob();
        if (cancelled) return;
        if (thumbnailUrlRef.current) URL.revokeObjectURL(thumbnailUrlRef.current);
        const url = URL.createObjectURL(blob);
        thumbnailUrlRef.current = url;
        setSignThumbnailUrl(url);
      } catch (_) {
        if (!cancelled) setSignThumbnailUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionUser]);

  useEffect(() => () => {
    if (thumbnailUrlRef.current) {
      URL.revokeObjectURL(thumbnailUrlRef.current);
      thumbnailUrlRef.current = '';
    }
  }, []);

  const parseApiError = async (err) => {
    if (err?.serverResponse) {
      const sr = err.serverResponse;
      if (sr.errors?.length) return sr.errors;
      if (sr.message) return [sr.message];
    }
    try {
      const data = await err.json?.();
      if (data?.errors) return data.errors;
      if (data?.message) return [data.message];
    } catch (_e) { /* ignore */ }
    return ['저장에 실패했습니다.'];
  };

  const mergePickerItems = async (idsArg) => {
    const ids = Array.isArray(idsArg) ? idsArg : pickerIds;
    if (!ids.length) {
      setErrors(['추가할 서류를 선택하세요.']);
      return;
    }
    setErrors([]);
    setInfo('');
    try {
      const built = await dispatch(subMatTemplateActions.buildItems(ids));
      setItems((prev) => {
        const { items: next, addedCount } = appendNewItemsSafely(built, prev);
        if (addedCount === 0) {
          setInfo('선택한 서류는 이미 목차에 있습니다.');
        } else if (prev.length === 0) {
          setInfo(`${addedCount}개 항목을 추가했습니다. 필요하면 드래그로 조정하세요.`);
        } else {
          setInfo(`${addedCount}개 항목을 맨 아래에 추가했습니다. 원하는 위치로 드래그하세요.`);
        }
        return next;
      });
      setPickerIds([]);
      setTocPanelOpen(true);
    } catch (err) {
      const msgs = await parseApiError(err);
      setErrors(msgs);
    }
  };

  const applyPickerSelection = (ids) => {
    const prevSet = new Set(pickerIds);
    const grew = ids.some((id) => !prevSet.has(id));
    const panelWasClosed = !tocPanelOpen;
    setPickerIds(ids);
    if (ids.length > 0) setTocPanelOpen(true);
    // 패널이 닫힌 상태에서 목차가 비어 있을 때만 첫 선택을 즉시 반영.
    // 목차를 먼저 연 뒤 선택하면 「추가」로만 반영.
    if (panelWasClosed && items.length === 0 && grew) mergePickerItems(ids);
  };

  const allFolderIds = (folders || []).map((f) => f.id);
  const allFoldersSelected = allFolderIds.length > 0
    && allFolderIds.every((id) => pickerIds.includes(id));
  const someFoldersSelected = pickerIds.length > 0 && !allFoldersSelected;

  const handleToggleSelectAllFolders = () => {
    if (allFoldersSelected) {
      setPickerIds([]);
      return;
    }
    applyPickerSelection(allFolderIds);
  };

  const handleNextStep = () => {
    if (!items.length) {
      setErrors(['목차 항목이 1개 이상 필요합니다.']);
      return;
    }
    setErrors([]);
    setInfo('');
    setStep(2);
  };

  const handlePrevStep = () => {
    setErrors([]);
    setInfo('');
    setStep(1);
  };

  const handleChangeRecipient = () => setEmptyRecipient(false);
  const handleChangeSite = () => setEmptySite(false);

  const runPreviewMerge = async ({ tocPreviewOnly = false } = {}) => {
    const recipient = (getValues('recipient') || '').trim();
    const site = (getValues('site') || '').trim();
    setMerging(true);
    setErrors([]);
    setInfo('');
    try {
      const mergePayload = buildMergePreviewPayload(
        items,
        name.trim() || '템플릿',
        tocNumbering,
        bodyHeaderPaging,
      );
      // 1단계: 목차 확인용 — 원본대조필/스탬프 없이 합본만
      const exportSignOption = tocPreviewOnly ? 'NONE' : signOption;
      const response = await dispatch(subMatTemplateActions.previewSendMerge({
        mailData: {
          ...mergePayload,
          stampPlacements: tocPreviewOnly
            ? {}
            : stampPlacementsToApi(stampPlacements, [], bodyHeaderPaging),
          myCertified: true,
          options: { signOption: exportSignOption, stampLayout: normalizeStampLayout(stampLayout) },
          phrases: {
            recipient: tocPreviewOnly ? '' : recipient,
            site: tocPreviewOnly ? '' : site,
            supplierLabel: getValues('supplierLabel'),
            supplier: getValues('supplier'),
            phrase: getValues('phrase'),
          },
        },
        addedFiles: [],
      }));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(name.trim() || '템플릿').replace(/[\\/:*?"<>|]/g, '_')}_합본.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const msgs = await parseApiError(err);
      setErrors(msgs.length ? msgs : [err.message || 'PDF로 내보내지 못했습니다.']);
    } finally {
      setMerging(false);
    }
  };

  const handlePreviewMerge = () => {
    if (!items.length) {
      setErrors(['목차 항목이 1개 이상 필요합니다.']);
      return;
    }
    // 1단계: 목차 확인용 — 제출처·현장·인감 검증 생략
    if (step === 1) {
      runPreviewMerge({ tocPreviewOnly: true });
      return;
    }
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN'
        && normalizeStampLayout(stampLayout) !== STAMP_LAYOUT_COMPACT) {
      if (!sessionUser?.signDocumentPath) {
        setErrors(['인감이 등록되지 않았습니다. 내정보에서 인감을 등록해 주세요.']);
        return;
      }
      const recipient = (getValues('recipient') || '').trim();
      const site = (getValues('site') || '').trim();
      if (!recipient || !site) {
        setEmptyRecipient(!recipient);
        setEmptySite(!site);
        setPdfEmptyConfirmOpen(true);
        return;
      }
    } else if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN' && !sessionUser?.signDocumentPath) {
      setErrors(['인감이 등록되지 않았습니다. 내정보에서 인감을 등록해 주세요.']);
      return;
    }
    runPreviewMerge();
  };

  const persistTemplate = async (saveName, saveMemo, { closeDialog = true } = {}) => {
    const trimmedName = (saveName || '').trim();
    if (!trimmedName) {
      return { ok: false, errors: ['템플릿 이름을 입력하세요.'] };
    }
    if (!items.length) {
      return { ok: false, errors: ['목차 항목이 1개 이상 필요합니다.'] };
    }
    setSaving(true);
    setErrors([]);
    setInfo('');
    const payload = {
      id: template?.id || uuidv4(),
      name: trimmedName,
      memo: (saveMemo || '').trim() || null,
      tocNumbering: normalizeTocNumbering(tocNumbering),
      bodyHeaderPaging: normalizeBodyHeaderPaging(bodyHeaderPaging),
      signOption: normalizeSignOption(signOption),
      stampLayout: normalizeStampLayout(stampLayout),
      stampPlacements: stampPlacementsToApi(stampPlacements, [], bodyHeaderPaging),
      items: items.map((it) => ({
        id: it.id || uuidv4(),
        folderId: it.folderId,
        parentItemId: it.parentItemId || null,
        titleOverride: it.titleOverride?.trim() || null,
        sortOrder: it.sortOrder ?? 0,
        included: it.included !== false,
        includeTestComparison: it.includeTestComparison === true,
      })),
    };
    try {
      if (isCreate) {
        await dispatch(subMatTemplateActions.create(payload));
      } else {
        await dispatch(subMatTemplateActions.modify(payload));
      }
      onSaved?.();
      if (closeDialog) {
        onClose();
      }
      return { ok: true };
    } catch (err) {
      const msgs = await parseApiError(err);
      return { ok: false, errors: msgs };
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const result = await persistTemplate(name, memo);
    if (!result.ok) setErrors(result.errors || []);
  };

  const openMetaDialog = (mode) => {
    setMetaName(name);
    setMetaMemo(memo);
    setMetaErrors([]);
    setMetaDialogMode(mode);
  };

  const handleSaveClick = () => {
    if (!items.length) {
      setErrors(['목차 항목이 1개 이상 필요합니다.']);
      return;
    }
    setErrors([]);
    if (isCreate) {
      openMetaDialog('createSave');
      return;
    }
    handleSaveEdit();
  };

  const handleSaveMeta = async () => {
    const trimmed = metaName.trim();
    if (!trimmed) {
      setMetaErrors(['템플릿 이름을 입력하세요.']);
      return;
    }
    setMetaErrors([]);
    if (metaDialogMode === 'editMeta') {
      const result = await persistTemplate(trimmed, metaMemo, { closeDialog: false });
      if (!result.ok) {
        setMetaErrors(result.errors || []);
        return;
      }
      setName(trimmed);
      setMemo(metaMemo);
      setMetaDialogMode(null);
      setInfo('이름·메모를 저장했습니다.');
      return;
    }
    setName(trimmed);
    setMemo(metaMemo);
    const result = await persistTemplate(trimmed, metaMemo);
    if (!result.ok) {
      setMetaErrors(result.errors || []);
      return;
    }
    setMetaDialogMode(null);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          ...DIALOG_PAPER_SX,
          // 1페이지: 창 높이를 채워 목차 미리보기를 키운다. 2페이지는 내용 높이만.
          ...(step === 1
            ? { height: { xs: 'calc(100vh - 32px)', md: 'calc(100vh - 64px)' } }
            : { height: 'auto' }),
        },
      }}
    >
      <DialogTitleClose onClose={onClose} sx={{ pr: 6, flexShrink: 0 }}>
        {isCreate ? (
          '템플릿 만들기'
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              pr: 1,
            }}
          >
            <Box
              component="span"
              title={name || undefined}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {`템플릿 수정 - ${name || ''}`}
            </Box>
            <Button
              size="small"
              variant="text"
              startIcon={<StickyNote2OutlinedIcon sx={{ fontSize: '22px !important' }} />}
              aria-label="메모 보기"
              onClick={(e) => setMemoPopoverAnchor(e.currentTarget)}
              sx={{
                flexShrink: 0,
                ml: 1.5,
                px: 0.75,
                minWidth: 0,
                color: 'text.secondary',
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              메모보기
            </Button>
            <Popover
              open={Boolean(memoPopoverAnchor)}
              anchorEl={memoPopoverAnchor}
              onClose={() => setMemoPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 1.5, maxWidth: 360 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  메모
                </Typography>
                <Typography
                  variant="body2"
                  color={memo ? 'text.primary' : 'text.secondary'}
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {memo || '등록된 메모가 없습니다.'}
                </Typography>
              </Box>
            </Popover>
            <Button
              size="small"
              variant="text"
              onClick={() => openMetaDialog('editMeta')}
              disabled={saving || merging}
              sx={{ flexShrink: 0, ml: 0.25 }}
            >
              이름메모 수정하기
            </Button>
          </Box>
        )}
      </DialogTitleClose>
      <DialogContent
        dividers
        sx={{
          px: 2,
          // theme.js `.MuiDialogContent-root { padding-top: 10px !important }` 덮어씀
          pt: '16px !important',
          pb: '16px !important',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {errors.map((e, i) => (
          <Typography key={i} color="error" variant="body2">{e}</Typography>
        ))}
        {info && (
          <Typography color="primary" variant="body2" sx={{ mb: 0.5 }}>{info}</Typography>
        )}

        {step === 1 && (
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: STEP1_WORK_MIN_HEIGHT,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: STEP1_WORK_MIN_HEIGHT,
                boxSizing: 'border-box',
              }}
            >
              <SubMatTemplateSendSection
                title={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography variant="subtitle2" component="span" noWrap>
                      서류 선택
                    </Typography>
                    <Box
                      component="label"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        cursor: allFolderIds.length ? 'pointer' : 'default',
                        flexShrink: 0,
                        userSelect: 'none',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allFoldersSelected}
                        indeterminate={someFoldersSelected}
                        disabled={!allFolderIds.length}
                        onChange={handleToggleSelectAllFolders}
                        inputProps={{ 'aria-label': '전체선택' }}
                        sx={{ p: 0.25 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        전체선택
                      </Typography>
                    </Box>
                  </Box>
                )}
                sx={{
                  height: '100%',
                  borderRadius: tocPanelOpen ? 0 : 1,
                }}
                contentSx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
              >
                <SubMatTemplateFolderPicker
                  folders={folders}
                  selectedIds={pickerIds}
                  onChange={applyPickerSelection}
                  bare
                />
              </SubMatTemplateSendSection>

              {/* 목차 책갈피 + 패널 — 한 덩어리로 오른쪽↔왼쪽 슬라이드 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  width: `calc(${TOC_PANEL_WIDTH} + ${TOC_BOOKMARK_WIDTH}px)`,
                  transform: tocPanelOpen
                    ? 'translateX(0)'
                    : `translateX(calc(100% - ${TOC_BOOKMARK_WIDTH}px))`,
                  transition: 'transform 0.28s ease',
                  willChange: 'transform',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={tocPanelOpen ? '목차 패널 접기' : '목차 패널 펼치기'}
                  aria-expanded={tocPanelOpen}
                  onClick={() => setTocPanelOpen((v) => !v)}
                  sx={{
                    pointerEvents: 'auto',
                    alignSelf: 'flex-start',
                    mt: `${TOC_SHELL_PAD}px`,
                    flexShrink: 0,
                    width: TOC_BOOKMARK_WIDTH,
                    minHeight: 96,
                    py: 1.5,
                    px: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    border: 1,
                    borderColor: TOC_SHELL_BG,
                    borderRight: 0,
                    // 패널에 붙는 쪽(우)은 직각, 바깥쪽(좌) 상·하 라운딩 — 외곽과 한 세트
                    borderRadius: '8px 0 0 8px',
                    bgcolor: TOC_SHELL_BG,
                    boxShadow: tocPanelOpen ? 'none' : 1,
                    cursor: 'pointer',
                    color: 'common.white',
                    '&:hover': {
                      bgcolor: 'grey.600',
                      color: 'common.white',
                    },
                  }}
                >
                  {tocPanelOpen
                    ? <ChevronRightIcon sx={{ fontSize: 18 }} />
                    : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      writingMode: 'vertical-rl',
                      letterSpacing: '0.12em',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    목차
                  </Typography>
                </Box>

                <Box
                  aria-hidden={!tocPanelOpen}
                  sx={{
                    pointerEvents: tocPanelOpen ? 'auto' : 'none',
                    flex: 1,
                    minWidth: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 8fr)' },
                    gap: 1,
                    p: `${TOC_SHELL_PAD}px`,
                    boxSizing: 'border-box',
                    bgcolor: TOC_SHELL_BG,
                    borderTop: 1,
                    borderBottom: 1,
                    borderRight: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    boxShadow: '-4px 0 12px rgba(0,0,0,0.06)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      minWidth: 0,
                      minHeight: 0,
                    }}
                  >
                    <SubMatTemplateSendSection
                      sx={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                      contentSx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                      title={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                          <Typography variant="subtitle2" component="span" noWrap>
                            목차 구성
                            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label="목차 구성 도움말"
                            onClick={(e) => setTocHelpAnchor(e.currentTarget)}
                            sx={{ p: 0.25 }}
                          >
                            <HelpOutlineIcon fontSize="small" color="action" />
                          </IconButton>
                          <Popover
                            open={Boolean(tocHelpAnchor)}
                            anchorEl={tocHelpAnchor}
                            onClose={() => setTocHelpAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          >
                            <Box sx={{ p: 1.5, maxWidth: 280 }}>
                              <Typography variant="body2" gutterBottom>
                                · 서류 선택으로 시작하면 첫 선택이 바로 목차에 반영됩니다. 목차 패널을 먼저 연 뒤에는 선택 후 「추가」하세요.
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                · 드래그로 위치를 조정합니다. (위·아래=형제 순서, 가운데=하위로 이동)
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                · 우클릭으로 이름 변경·제거할 수 있습니다.
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                · 목차 항목 1개 이상 필수입니다.
                              </Typography>
                            </Box>
                          </Popover>
                        </Box>
                      )}
                      action={(
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={mergePickerItems}
                          disabled={!pickerIds.length}
                          sx={{
                            minHeight: 28,
                            py: 0.25,
                            px: 1,
                            fontSize: '0.8125rem',
                            lineHeight: 1.2,
                          }}
                        >
                          추가
                        </Button>
                      )}
                    >
                      <SubMatTemplateTocTree
                        items={items}
                        onChange={setItems}
                        bare
                        onPreview={tocDocPreview.previewItem}
                        previewLoadingId={tocDocPreview.loadingId}
                      />
                    </SubMatTemplateSendSection>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                    <SubMatTemplateSendSection
                      title={(
                        <SubMatTemplateTocPreviewTitle
                          pane={tocDocPreview.pane}
                          onPane={tocDocPreview.setPane}
                          docTitle={tocDocPreview.doc?.title}
                          onCloseDoc={tocDocPreview.closeDoc}
                        />
                      )}
                      sx={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                      contentSx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        bgcolor: tocDocPreview.pane === 'doc' ? 'background.paper' : 'grey.700',
                        p: 0,
                        boxSizing: 'border-box',
                      }}
                    >
                      {tocDocPreview.pane === 'doc' ? (
                        <SubMatTemplateTocDocFrame
                          url={tocDocPreview.doc?.url}
                          error={tocDocPreview.error}
                          loading={Boolean(tocDocPreview.loadingId)}
                        />
                      ) : (
                        <SubMatTemplateTocPreview
                          fillHeight
                          bare
                          documentLook
                          items={items}
                          tocNumbering={tocNumbering}
                        />
                      )}
                    </SubMatTemplateSendSection>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              maxWidth: 560,
              mx: 'auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              py: 1,
            }}
          >
            <SubMatTemplateSendSection
              title="원본대조필"
              sx={{ width: '100%' }}
              contentSx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CertifiedStampPrintPane
                signOption={signOption}
                stampLayout={stampLayout}
                onChangeSignOption={setSignOption}
                onChangeStampLayout={setStampLayout}
                control={control}
                signThumbnailUrl={signThumbnailUrl}
                emptyRecipient={emptyRecipient}
                emptySite={emptySite}
                onChangeRecipient={handleChangeRecipient}
                onChangeSite={handleChangeSite}
                captionCompact="원본대조필과 인감만 인쇄합니다."
                captionFull="제출처·현장명은 PDF 미리보기/내보내기에만 쓰이며, 템플릿에는 저장되지 않습니다."
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenWithIcon />}
                  onClick={() => setStampPlacementOpen(true)}
                  disabled={!items.length}
                  sx={{ mt: 1 }}
                >
                  문서에서 위치 지정
                </Button>
                {Object.keys(stampPlacements).length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    위치 {Object.keys(stampPlacements).length}곳 지정됨
                  </Typography>
                ) : null}
              </CertifiedStampPrintPane>
            </SubMatTemplateSendSection>
          </Box>
        )}
      </DialogContent>
      <SubMatTemplateDialogFooter
        cancelDisabled={saving || merging}
        onCancel={onClose}
        pdfButton={(
          <SubMatDialogFooterButton
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePreviewMerge}
            loading={merging}
            disabled={saving || !items.length}
          >
            PDF 보기
          </SubMatDialogFooterButton>
        )}
        beforePrimary={step === 2 ? (
          <SubMatDialogFooterButton
            variant="outlined"
            onClick={handlePrevStep}
            disabled={saving || merging}
          >
            이전
          </SubMatDialogFooterButton>
        ) : null}
        primaryButton={step === 1 ? (
          <SubMatDialogFooterButton
            variant="contained"
            onClick={handleNextStep}
            disabled={saving || merging}
          >
            다음
          </SubMatDialogFooterButton>
        ) : (
          <SubMatDialogFooterButton
            variant="contained"
            onClick={handleSaveClick}
            disabled={saving || merging}
          >
            저장
          </SubMatDialogFooterButton>
        )}
      />

      <CertifiedStampPlacementDialog
        open={stampPlacementOpen}
        onClose={() => setStampPlacementOpen(false)}
        items={items}
        extras={[]}
        tocNumbering={tocNumbering}
        bodyHeaderPaging={bodyHeaderPaging}
        headerLeft={sessionUser?.name}
        signThumbnailUrl={signThumbnailUrl}
        placements={stampPlacements}
        onChangePlacements={setStampPlacements}
        stampLayout={stampLayout}
      />
    </Dialog>

    <Dialog
      open={metaDialogOpen}
      onClose={() => !saving && setMetaDialogMode(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {metaDialogMode === 'editMeta' ? '이름·메모 수정' : '템플릿 저장'}
      </DialogTitle>
      <DialogContent>
        {metaErrors.map((e, i) => (
          <Typography key={i} color="error" variant="body2" sx={{ mb: 0.5 }}>{e}</Typography>
        ))}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="템플릿 이름"
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              required
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveMeta();
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="메모"
              value={metaMemo}
              onChange={(e) => setMetaMemo(e.target.value)}
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSaveMeta} variant="contained" disabled={saving}>
          저장
        </Button>
        <Button onClick={() => setMetaDialogMode(null)} disabled={saving}>
          취소
        </Button>
      </DialogActions>
    </Dialog>

    <ConfirmDialog
      title="안내"
      open={pdfEmptyConfirmOpen}
      setOpen={setPdfEmptyConfirmOpen}
      onConfirm={() => runPreviewMerge()}
      onCancel={() => {}}
      confirmLabel="확인"
      cancelLabel="취소"
    >
      제출처·현장명이 비어 있습니다. 이 PDF를 그대로 외부에 전달하면 원본대조필 정보가 불완전할 수 있습니다. 계속할까요?
    </ConfirmDialog>

    <AlertDialog
      alertInfo={alertInfo}
      setAlertInfo={setAlertInfo}
    />
    </>
  );
};

export default SubMatTemplateEditDialog;
